import React, { useState, useEffect, useRef } from 'react';
import CountUp from 'react-countup';

const About = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.3, // Bölümün %30'u görünür olduğunda tetiklenir
                rootMargin: '0px 0px -50px 0px' // Biraz daha geç tetiklenmesi için
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, [isVisible]);

    return (
        <section
            ref={sectionRef}
            id="about"
            className="min-h-screen flex flex-col justify-center pt-20 pb-12 bg-cover bg-center relative"
            style={{ backgroundImage: 'url("/Images/about-background.jpg")' }}
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col gap-16 md:gap-8 lg:gap-12">

                {/* Desktop Layout */}
                <div className="hidden lg:flex items-center justify-center gap-16 xl:gap-32 2xl:gap-64">
                    {/* Left Side */}
                    <div className="flex flex-col gap-2 items-center">
                        <h3 className="text-[#ad2831] uppercase font-montserrat text-lg xl:text-xl">
                            Yılların Birikiminin Neticesi..
                        </h3>
                        <h1 className="text-white text-center text-3xl xl:text-4xl font-montserrat">
                            Zenon Oyuncu
                        </h1>
                        <span className="uppercase text-4xl xl:text-5xl font-bold text-white mt-2">
                            bilgisayarları
                        </span>
                    </div>

                    {/* Right Side - Statistics Grid */}
                    <div className="grid text-white grid-cols-2 gap-0 max-w-md xl:max-w-lg">
                        <div className="p-4 xl:p-6 flex flex-col items-center gap-2 border-r border-b border-white/50">
                            {isVisible ? (
                                <CountUp start={0} end={1999} duration={2} separator="" className="font-bold text-3xl xl:text-5xl" />
                            ) : (
                                <span className="font-bold text-3xl xl:text-5xl">0</span>
                            )}
                            <p className="text-sm xl:text-xl uppercase text-center">kuruluş yılı</p>
                        </div>
                        <div className="p-4 xl:p-6 flex flex-col items-center gap-2 border-b border-white/50">
                            {isVisible ? (
                                <CountUp start={0} end={20000} duration={2} separator="," className="font-bold text-3xl xl:text-5xl" />
                            ) : (
                                <span className="font-bold text-3xl xl:text-5xl">0</span>
                            )}
                            <p className="text-sm xl:text-xl uppercase text-white/80 text-center">Mutlu Müşteri</p>
                        </div>
                        <div className="p-4 xl:p-6 flex flex-col items-center gap-2 border-r border-b border-white/50">
                            {isVisible ? (
                                <CountUp start={0} end={3000} duration={2} separator="" className="font-bold text-3xl xl:text-5xl" />
                            ) : (
                                <span className="font-bold text-3xl xl:text-5xl">0</span>
                            )}
                            <p className="text-sm xl:text-xl uppercase text-white/80 text-center">Firma Bizimle Çalışıyor</p>
                        </div>
                        <div className="p-4 xl:p-6 flex flex-col items-center gap-2 border-b border-white/50">
                            {isVisible ? (
                                <CountUp start={0} end={2} duration={2} separator="" className="font-bold text-3xl xl:text-5xl" />
                            ) : (
                                <span className="font-bold text-3xl xl:text-5xl">0</span>
                            )}
                            <p className="text-sm xl:text-xl uppercase text-white/80 text-center">ofislerimiz</p>
                        </div>
                        <div className="p-4 xl:p-6 flex flex-col items-center gap-2 border-r border-white/50">
                            {isVisible ? (
                                <CountUp start={0} end={60} duration={2} separator="" className="font-bold text-3xl xl:text-5xl" />
                            ) : (
                                <span className="font-bold text-3xl xl:text-5xl">0</span>
                            )}
                            <p className="text-sm xl:text-xl uppercase text-white/80 text-center">takım üyeleri</p>
                        </div>
                        <div className="p-4 xl:p-6 flex flex-col items-center gap-2">
                            <div>
                                {isVisible ? (
                                    <>
                                        <CountUp start={0} end={7000} duration={2} separator="" className="font-bold text-3xl xl:text-5xl" />
                                        <span className="text-3xl xl:text-5xl">+</span>
                                    </>
                                ) : (
                                    <span className="font-bold text-3xl xl:text-5xl">0+</span>
                                )}
                            </div>
                            <p className="text-sm xl:text-xl uppercase text-white/80 text-center">tamamlanan proje</p>
                        </div>
                    </div>
                </div>

                {/* Mobile & Tablet Layout */}
                <div className="lg:hidden flex flex-col items-center space-y-8">
                    {/* Header Section */}
                    <div className="flex flex-col gap-3 items-center text-center">
                        <h3 className="text-[#ad2831] uppercase font-montserrat text-sm sm:text-base md:text-lg">
                            Yılların Birikiminin Neticesi..
                        </h3>
                        <h1 className="text-white text-2xl sm:text-3xl md:text-4xl font-montserrat">
                            Zenon Oyuncu
                        </h1>
                        <span className="uppercase text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                            bilgisayarları
                        </span>
                    </div>

                    {/* Statistics Grid - Mobile */}
                    <div className="w-full max-w-sm sm:max-w-md md:max-w-lg">
                        <div className="grid text-white grid-cols-2 gap-0">
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-2 border-r border-b border-white/50">
                                {isVisible ? (
                                    <CountUp start={0} end={1999} duration={2} separator="" className="font-bold text-2xl sm:text-3xl md:text-4xl" />
                                ) : (
                                    <span className="font-bold text-2xl sm:text-3xl md:text-4xl">0</span>
                                )}
                                <p className="text-xs sm:text-sm md:text-base uppercase text-center">kuruluş yılı</p>
                            </div>
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-2 border-b border-white/50">
                                {isVisible ? (
                                    <CountUp start={0} end={20000} duration={2} separator="," className="font-bold text-2xl sm:text-3xl md:text-4xl" />
                                ) : (
                                    <span className="font-bold text-2xl sm:text-3xl md:text-4xl">0</span>
                                )}
                                <p className="text-xs sm:text-sm md:text-base uppercase text-white/80 text-center">Mutlu Müşteri</p>
                            </div>
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-2 border-r border-b border-white/50">
                                {isVisible ? (
                                    <CountUp start={0} end={3000} duration={2} separator="" className="font-bold text-2xl sm:text-3xl md:text-4xl" />
                                ) : (
                                    <span className="font-bold text-2xl sm:text-3xl md:text-4xl">0</span>
                                )}
                                <p className="text-xs sm:text-sm md:text-base uppercase text-white/80 text-center leading-tight">Firma Bizimle Çalışıyor</p>
                            </div>
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-2 border-b border-white/50">
                                {isVisible ? (
                                    <CountUp start={0} end={2} duration={2} separator="" className="font-bold text-2xl sm:text-3xl md:text-4xl" />
                                ) : (
                                    <span className="font-bold text-2xl sm:text-3xl md:text-4xl">0</span>
                                )}
                                <p className="text-xs sm:text-sm md:text-base uppercase text-white/80 text-center">ofislerimiz</p>
                            </div>
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-2 border-r border-white/50">
                                {isVisible ? (
                                    <CountUp start={0} end={60} duration={2} separator="" className="font-bold text-2xl sm:text-3xl md:text-4xl" />
                                ) : (
                                    <span className="font-bold text-2xl sm:text-3xl md:text-4xl">0</span>
                                )}
                                <p className="text-xs sm:text-sm md:text-base uppercase text-white/80 text-center">takım üyeleri</p>
                            </div>
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-2">
                                <div>
                                    {isVisible ? (
                                        <>
                                            <CountUp start={0} end={7000} duration={2} separator="" className="font-bold text-2xl sm:text-3xl md:text-4xl" />
                                            <span className="text-2xl sm:text-3xl md:text-4xl">+</span>
                                        </>
                                    ) : (
                                        <span className="font-bold text-2xl sm:text-3xl md:text-4xl">0+</span>
                                    )}
                                </div>
                                <p className="text-xs sm:text-sm md:text-base uppercase text-white/80 text-center leading-tight">tamamlanan proje</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
}

export default About;